let audioCtx: AudioContext | null = null;
let toastContainer: HTMLElement | null = null;

const SEVERITY_COLORS: Record<string, string> = {
  low: 'border-slate-500/40 bg-th-deep',
  medium: 'border-amber-500/40 bg-th-deep',
  high: 'border-orange-500/40 bg-th-deep',
  critical: 'border-red-500/50 bg-th-deep',
};

export interface ToastOptions {
  severity?: string;
  title: string;
  description?: string;
  meta?: string[];
  icon?: string;
  onClick?: () => void;
  duration?: number;
}

export function initToastSystem() {
  if (document.getElementById('toast-container')) return;
  const el = document.createElement('div');
  el.id = 'toast-container';
  el.className = 'fixed top-4 right-4 left-4 sm:left-auto z-[9999] flex flex-col gap-3 pointer-events-none';
  el.style.maxWidth = '400px';
  document.body.appendChild(el);
  toastContainer = el;
}

export function showToast(opts: ToastOptions) {
  if (!toastContainer) initToastSystem();
  const severity = opts.severity || 'medium';
  const colorCls = SEVERITY_COLORS[severity] || SEVERITY_COLORS.medium;
  const toast = document.createElement('div');
  toast.className = `pointer-events-auto rounded-xl border p-4 shadow-2xl backdrop-blur-sm cursor-pointer transition-all duration-300 transform translate-x-[120%] ${colorCls}`;

  const iconHtml = opts.icon || alertPulseIcon(severity);
  const metaHtml = opts.meta?.length ? `<div class="flex items-center gap-3 mt-2 text-xs text-th-text-3">${opts.meta.map(m => `<span>${m}</span>`).join('')}</div>` : '';

  toast.innerHTML = `
    <div class="flex items-start gap-3">
      <div class="shrink-0 mt-0.5">${iconHtml}</div>
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 mb-1">
          <span class="text-sm font-semibold text-th-text">${opts.title}</span>
        </div>
        ${opts.description ? `<p class="text-xs text-th-text-2 leading-relaxed">${opts.description}</p>` : ''}
        ${metaHtml}
      </div>
      <button class="shrink-0 text-th-text-3 hover:text-th-text-2 transition-colors" aria-label="Dismiss">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>
    </div>
  `;

  toast.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('button')) { dismissToast(toast); return; }
    opts.onClick?.();
    dismissToast(toast);
  });

  toastContainer!.appendChild(toast);
  requestAnimationFrame(() => { toast.classList.remove('translate-x-[120%]'); toast.classList.add('translate-x-0'); });
  setTimeout(() => dismissToast(toast), opts.duration ?? 8000);
}

export function dismissToast(toast: HTMLElement) {
  toast.classList.remove('translate-x-0');
  toast.classList.add('translate-x-[120%]', 'opacity-0');
  setTimeout(() => toast.remove(), 300);
}

function alertPulseIcon(severity: string): string {
  const color = severity === 'critical' ? 'text-red-400' :
                severity === 'high' ? 'text-orange-400' :
                severity === 'medium' ? 'text-amber-400' : 'text-slate-400';
  return `<div class="relative">
    <svg class="w-5 h-5 ${color}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
    </svg>
    ${(severity === 'critical' || severity === 'high') ? `<span class="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 animate-ping"></span><span class="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500"></span>` : ''}
  </div>`;
}

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioCtx;
}

export function playAlertSound(severity: string) {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') ctx.resume();
    const isCritical = severity === 'critical' || severity === 'high';
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    if (isCritical) {
      osc.type = 'square';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(660, ctx.currentTime + 0.15);
      osc.frequency.setValueAtTime(880, ctx.currentTime + 0.3);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } else {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(520, ctx.currentTime);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    }
  } catch {}
}

export function requestNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

export function showBrowserNotification(title: string, body: string, opts?: { tag?: string; requireInteraction?: boolean; onClick?: () => void }) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const n = new Notification(title, {
    body,
    tag: opts?.tag,
    requireInteraction: opts?.requireInteraction,
  });
  if (opts?.onClick) {
    n.onclick = () => { window.focus(); opts.onClick!(); n.close(); };
  }
}
